import { useState, useEffect, useCallback } from "react";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  Method,
} from "axios";

interface UseApiProps {
  method: Method;
  url: string;
  payload?: any;
  params?: any;
  headers?: Record<string, string>;
  enabled?: boolean;
  deps?: any[];
  throwError?: boolean
}

export const useApi = <T = any>({
  method,
  url,
  payload,
  params,
  headers,
  enabled = false,
  deps = [],
  throwError,
}: UseApiProps) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const apiCall = useCallback(
    async (override?: Partial<UseApiProps>) => {
      try {
        setLoading(true);
        setError(null);

        const config: AxiosRequestConfig = {
          method: override?.method ?? method,
          url: override?.url ?? url,
          data: override?.payload ?? payload,
          params: override?.params ?? params,
          headers: override?.headers ?? headers,
        };

        const response = await axios(config);

        setData(response.data);

        return response.data;
      } catch (err) {
        const axiosError = err as AxiosError;

        setError(axiosError);

        const shouldThrow =
          override?.throwError ?? throwError ?? false;

        if (shouldThrow) {
          throw axiosError;
        }

        return null;
      } finally {
        setLoading(false);
      }
    },
    [method, url, payload, params, headers, throwError]
  );

  useEffect(() => {
    if (enabled) {
      apiCall();
    }
  }, [enabled, apiCall]);

  return {
    data,
    loading,
    error,
    apiCall,
    setData,
  };
};